import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Check, X, ExternalLink, Unplug } from 'lucide-react';
import { toast } from 'sonner';

export function QBConnectButton({ settings, onRefresh }) {
  const { api } = useAuth();
  const { lang } = useLanguage();
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const isConnected = settings?.quickbooks_enabled && settings?.quickbooks_connected_at;
  const companyName = settings?.quickbooks_company_name;

  // Listen for OAuth popup messages
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'qb-connected') {
        setConnecting(false);
        toast.success(
          lang === 'ru'
            ? `QuickBooks подключён: ${event.data.companyName}`
            : `QuickBooks connected: ${event.data.companyName}`
        );
        onRefresh?.();
      } else if (event.data?.type === 'qb-error') {
        setConnecting(false);
        toast.error(`QuickBooks error: ${event.data.error}`);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [lang, onRefresh]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.get('/qb/auth-url');
      const { auth_url } = res.data;

      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      window.open(
        auth_url,
        'qb-oauth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );
    } catch (err) {
      setConnecting(false);
      toast.error(err.response?.data?.detail || 'Failed to start QuickBooks connection');
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await api.post('/qb/disconnect');
      toast.success(lang === 'ru' ? 'QuickBooks отключён' : 'QuickBooks disconnected');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      const res = await api.get('/qb/company-info');
      toast.success(
        lang === 'ru'
          ? `Подключение работает: ${res.data.company_name}`
          : `Connection works: ${res.data.company_name}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Connection test failed');
    }
  };

  if (isConnected) {
    return (
      <Card className="border-green-200 dark:border-green-800">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <div className="font-medium text-sm">
                  {lang === 'ru' ? 'QuickBooks подключён' : 'QuickBooks connected'}
                </div>
                <div className="text-xs text-muted-foreground">{companyName}</div>
              </div>
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                {lang === 'ru' ? 'Активен' : 'Active'}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleTestConnection}>
                {lang === 'ru' ? 'Тест' : 'Test'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-dashed">
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-sm">
              {lang === 'ru' ? 'QuickBooks не подключён' : 'QuickBooks not connected'}
            </div>
            <div className="text-xs text-muted-foreground">
              {lang === 'ru'
                ? 'Подключите для синхронизации платежей и расходов'
                : 'Connect to sync payments and expenses'
              }
            </div>
          </div>
          <Button onClick={handleConnect} disabled={connecting}>
            {connecting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <ExternalLink className="h-4 w-4 mr-2" />
            )}
            {lang === 'ru' ? 'Подключить QuickBooks' : 'Connect QuickBooks'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
